import { useAuth0 } from "@auth0/auth0-react";
import { cloneDeep } from "lodash";
import { useEffect } from "react";

import { getAudience, getAudiences } from "@/api";
import { useAudienceContext, useDemo } from "@/context";
import { Audience } from "@/types";
import { defaultBuildAudience, defaultUploadAudience, demoClients } from "@/utils";

export const useAudience = <T extends Audience["audience_type"]>(
  audienceId: string | undefined,
  audienceType?: T
): {
  audience: T extends Audience["audience_type"]
    ? Extract<Audience, { audience_type: T }>
    : Audience | null;
  loading: boolean;
  error: string | null;
} => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {

    const fetchAudience = async (id: string) => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getAudience(
          accessToken,
          id,
          audienceType ?? ""
        );

        if (data) {
          if (!audienceType || data.audience_type === audienceType) {
            dispatch({ type: "GET_AUDIENCE", payload: { audience: data } });
          } else {
            dispatch({ type: "ERROR", payload: "Unable to load audience" });
          }
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    if (!audienceId) {
      dispatch({
        type: "GET_AUDIENCE",
        payload: {
          audience:
            audienceType === "build"
              ? cloneDeep(defaultBuildAudience)
              : cloneDeep(defaultUploadAudience),
          built: false,
        },
      });
      return;
    } else if (state.currentAudience?.audience_id === audienceId) {
      return;
    } else {
      fetchAudience(audienceId);
    }
  }, [audienceId, audienceType, getAccessTokenSilently]);

  return {
    audience: (state.currentAudience ??
      null) as T extends Audience["audience_type"]
      ? Extract<Audience, { audience_type: T }>
      : Audience | null,
    loading: state.loading,
    error: state.error,
  };
};

export const useAudiences = (audienceType?: string) => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  const client = process.env.REACT_APP_CLIENT || "core";
  const { isDemoMode, demoClient } = useDemo();

  useEffect(() => {
    const fetchAudiences = async () => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getAudiences(
          accessToken,
          audienceType ?? "",
          client,
          isDemoMode ? demoClients.find(x => x.name === demoClient)?.label : undefined
        );
        if (data) {
          dispatch({ type: "GET_AUDIENCE_LIST", payload: data });
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    fetchAudiences();
    // if (!state.audiences) {
    //   fetchAudiences();
    // }
  }, [audienceType, client, dispatch, getAccessTokenSilently, isDemoMode, demoClient]);

  return {
    audiences: state.audiences,
    loading: state.loading,
    error: state.error,
  };
};
